import { useEffect, useMemo } from 'react';
import './PlatformDrawer.css';

export type PlatformDrawerAction = {
  id?: string;
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
  disabled?: boolean;
};

type PlatformDrawerProps = {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  children?: React.ReactNode;
  closeOnOverlayClick?: boolean;

  /**
   * Si se pasa `actions`, se renderizan tal cual.
   * Si no, se usan por defecto: Apply / Cancel.
   */
  actions?: PlatformDrawerAction[];
  onApply?: () => void;
  onCancel?: () => void;
};

export function PlatformDrawer({
  isOpen,
  title,
  onClose,
  children,
  closeOnOverlayClick = true,
  actions,
  onApply,
  onCancel,
}: PlatformDrawerProps) {
  const derivedActions = useMemo<PlatformDrawerAction[]>(
    () =>
      actions ?? [
        {
          id: 'apply',
          label: 'Apply',
          variant: 'primary',
          onClick: onApply ?? onClose,
        },
        {
          id: 'cancel',
          label: 'Cancel',
          variant: 'secondary',
          onClick: onCancel ?? onClose,
        },
      ],
    [actions, onApply, onCancel, onClose]
  );

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  return (
    <div
      className={`platform-drawer-layer ${isOpen ? 'platform-drawer-layer-open' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        pointerEvents: isOpen ? 'auto' : 'none',
        visibility: isOpen ? 'visible' : 'hidden',
      }}
    >
      <button
        type="button"
        className="platform-drawer-overlay"
        aria-label="Cerrar panel"
        onClick={() => {
          if (closeOnOverlayClick) onClose();
        }}
        style={{ opacity: isOpen ? 1 : 0 }}
      />

      <aside
        className="platform-drawer-sidebar"
        aria-label={title}
        style={{
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        <div className="platform-drawer-header">
          <h3>{title}</h3>
          <button
            type="button"
            className="platform-drawer-close"
            aria-label="Cerrar panel"
            onClick={onClose}
          >
            <span className="material-icons" aria-hidden="true">
              close
            </span>
          </button>
        </div>

        <div className="platform-drawer-content">{children}</div>

        <div className="platform-drawer-footer" role="group" aria-label="Acciones del drawer">
          {derivedActions.map((action, idx) => (
            <button
              key={action.id ?? `${action.label}-${idx}`}
              type="button"
              className={`platform-drawer-action-btn ${
                action.variant === 'primary' ? 'is-primary' : 'is-secondary'
              }`}
              disabled={action.disabled}
              onClick={action.onClick}
            >
              {action.label}
            </button>
          ))}
        </div>
      </aside>
    </div>
  );
}

